import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { GetFilterInput } from '@deltek/specification-client/core/Models/projectItem'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { SpecTable } from 'bpm/components/SpecTable'
import { SpecTableTitleSettingsProps } from 'bpm/components/SpecTable/SpecTableTitleSettings'
import {
    SpecColumn,
    SpecTableState,
} from 'bpm/components/SpecTable/Spectable.types'
import { TableSettingsProps } from 'bpm/components/SpecTable/settings/TableSettings'
import useSpecTableOptions, {
    UseSpecTableOptionProps,
} from 'bpm/components/SpecTable/useSpecTableOptions'
import BarIcon from 'components/Reports/BarIcon'
import he from 'he'
import { SmartReportDivision, SmartReportsItem } from 'models/projectReports'
import { TableReportItem } from 'models/reports'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import { ProjectFilterContext } from 'pages/ProjectsPage/ProjectsHomePage/components/ProjectFilterContext'
import React, { useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CellProps, Column } from 'react-table'
import { formatLocalDateTime } from 'shared/utils/dateUtils'
import { ReportTypes } from '..'

const useStyles = makeStyles((theme: Theme) => ({
    specTableStyle: {
        '& .table-title-container': {
            fontSize: pxToRem(22),
            textTransform: 'capitalize',
            marginLeft: theme.spacing(-1.5),
            color: '#153D60',
            fontWeight: '500',
        },
        '& .expand-collapse-all-divider, .filter-option-divider': {
            display: 'none',
        },
        '& .MuiSvgIcon-root.arrow-down, .MuiSvgIcon-root.arrow-right': {
            marginRight: theme.spacing(0.5),
            color: '#545454',
            fontSize: pxToRem(22),
        },
        '& .MuiTableSortLabel-active .MuiTableSortLabel-icon': {
            color: '#545454 !important',
            marginLeft: pxToRem(-12),
            fontSize: pxToRem(20),
        },
        '& .MuiGrid-justify-content-xs-space-between': {
            backgroundColor: theme.palette.common.white,
            color: '#153D60',
        },
        '& .MuiList-padding': {
            maxHeight: 300,
        },
        '& .table-title-settings-container': {
            backgroundColor: theme.palette.common.white,
            paddingBottom: pxToRem(10),
            paddingRight: 0,
        },
        '& .filter-option-container .filter-option-button .view-filter': {
            color: '#153D60',
        },
        '& .light button.expand-all-button, .light button.collapse-all-button': {
            color: '#153D60',
            fontWeight: 500,
            border: `1px solid #C9C9C9`,
        },
        '& .MuiButton-containedPrimary': {
            backgroundColor: '#343334',
            color: theme.palette.common.white,
        },
        '& .view-as-table.active, .view-filter.active': {
            backgroundColor: theme.palette.primary.main,
        },
        '& .load-next-button': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
        },
        '& .tr.grouped-row': {
            border: '1px solid #DADADA',
            borderRadius: '4px',
            marginTop: pxToRem(8),
            width: '100%',
        },
        '& .tr.grouped-row.expanded': {
            borderRadius: '4px 4px 0 0',
        },
        '& .tr.grouped-row.expanded .td.grouped-cell': {
            backgroundColor: theme.palette.common.white,
        },
        '& .tr': {
            border: '1px solid #DADADA',
            borderTop: 'none',
        },
        '& .tr:not(.tr.grouped-row):not(.header .tr):nth-child(odd)': {
            background: '#F8F8F8',
        },

        '& .header .tr:first-child': {
            borderRadius: 0,
            borderLeft: 'none',
            borderRight: 'none',
            fontWeight: 500,
            marginBottom: 10,
        },

        '& .header .tr:first-child div': {
            background: theme.palette.common.white,
            color: '#153D60',
        },
        '& .tr:not(.tr.grouped-row):not(.header .tr):not(.tr:last-child):not(.tr:last-child(-1))': {
            borderTop: 'none !important',
            borderBottom: 'none',
        },
        '& .tr:has(+.tr.grouped-row):not(.tr.grouped-row)': {
            borderBottom: '1px solid #C9C9C9 !important',
            borderRadius: '0 0 4px 4px',
        },
        '& .tr:has(+[class*="hiddenTr"]):not(.tr.grouped-row)': {
            borderBottom: '1px solid #C9C9C9 !important',
            borderRadius: '0 0 4px 4px',
        },
        '& .tr .th:first-child': {
            width: '10px !important',
            flex: '90 0 auto !important',
        },
        '& .td.grouped-cell': {
            color: '#153D60',
            fontWeight: '500',
            fontSize: pxToRem(16),
            backgroundColor: 'unset !important',
        },

        '& .td.grouped-cell .grouped-text': {
            marginTop: pxToRem(2),
            color: '#153D60',
            fontWeight: '500 !important', //  this enforces the semibold fontweight for expand/collapse button text
            fontSize: '16px',
        },
        '& .td.grouped-cell button': {
            border: 'none',
            background: 'none',
            padding: 0,
            '&:focus-visible': {
                outline: 'auto',
            },
        },
        '& .tr .td:first-child': {
            width: '10px !important',
            flex: '90 0 auto !important',
        },
        '& .body .tr:last-child': {
            marginTop: pxToRem(6),
        },
        '& .tr .td': {
            backgroundColor: 'unset !important',
        },
        '& .tr .grouped-cell.td:first-child': {
            width: 'auto !important',
            position: 'absolute !important', //this enforces the Grouped row title(First child) to get separated from sibling row cells, so that it would not push other row cells
            zIndex: '2 !important', //  this enables click on it the Grouped row title/toggle
        },
        '& .spectable-loading': {
            width: 2580,
        },
        '& .header': {
            position: 'absolute',
        },
        '& .sticky': {
            height: 'calc(100vh - 180px) !important',
            border: 'none',
        },
        '& .body': {
            maxHeight: 800,
            height: 710,
            marginTop: pxToRem(-10),
            width: 'fit-content',
        },
        '& .sticky .header': {
            zIndex: 5,
        },
        '& .full-scroll': {
            overflowY: 'auto',
        },
    },

    tableViewCont: {
        paddingTop: 0,
    },

    greyCellText: {
        fontWeight: 400,
        color: '#595959',
        '& svg': {
            marginRight: pxToRem(4),
        },
        paddingLeft: pxToRem(7),
    },
    blueLinkText: {
        fontWeight: 400,
        color: '#1899D4',
        marginLeft: pxToRem(7),
    },
    pillText: {
        fontWeight: 500,
        color: '#1899D4',
        border: '1px solid #1899D4',
        borderRadius: '20px',
        padding: '1px 8px',
        marginLeft: pxToRem(7),
        background: 'none',
        cursor: 'default !important',
    },
    clickable: {
        cursor: 'pointer !important',
    },
    offsetSpanForTooltip: {
        display: 'block',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        border: 0,
        background: 'none',
    },
    buttonOffsetTooltip: {
        display: 'block',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        background: 'none',
        border: 0,
        padding: 0,
        minWidth: '200px',
        maxWidth: '100%',
        textAlign: 'left',
    },
    barIcon: {
        marginLeft: pxToRem(-7),
        marginRight: `${pxToRem(3)} !important`,
    },
}))

const initialSorting = [{ id: 'dataType', desc: true }]
const groupBy = ['groupName']

interface ProjectTableViewProps {
    fetchNextData?: () => void
    fetchData: () => void
    exportReports?: () => void
    generateReport?: () => void
    reportsData?: SmartReportsItem[]
    divisions?: SmartReportDivision[]
    lastUpdate?: string
    tableDataLoading?: boolean
    isExportLoading?: boolean
}
const ProjectReportsTable: React.FC<ProjectTableViewProps> = ({
    fetchNextData,
    fetchData,
    reportsData,
    divisions,
    generateReport,
    lastUpdate,
    tableDataLoading,
    isExportLoading,
    exportReports,
}) => {
    const classes = useStyles()

    const { projectData } = React.useContext(ProjectPageContext)

    const { projectFilterValues, setProjectFilterValues } = React.useContext(
        ProjectFilterContext
    )
    const [inputFilterValues, setInputFilterValues] = React.useState<
        Partial<GetFilterInput>[]
    >(projectFilterValues)

    const [tableState, setTableState] = useState<
        SpecTableState<TableReportItem>
    >()

    const divContainer = useRef<HTMLDivElement>()

    const { projectId } = useParams<{ projectId?: string }>()
    // this call back is used to open the hotlinks/productType in the table column in new tab
    const openProjectWorkspaceById = React.useCallback((id) => {
        window.open(`/projects/${projectId}/workspace/${id}`, '_blank')
    }, [])

    const SpanTextWithTooltip = (text: string, width: string) => {
        if (text === '-') {
            return <span className={classes.greyCellText}>{text}</span>
        }

        return (
            <HoverTooltip text={text} enterDelay={700} enterNextDelay={700}>
                <span
                    className={`${classes.greyCellText} ${classes.offsetSpanForTooltip}`}
                    style={{ minWidth: width }}
                >
                    {text}
                </span>
            </HoverTooltip>
        )
    }

    const extractAndFormatDate = (text: string) => {
        const datePattern = /\b[A-Za-z]{3} \d{2}, \d{4} - \d{2}:\d{2} [APM]{2}\b/

        // Match the date part in the text
        const match = text.match(datePattern)

        if (match) {
            const datePart = match[0]
            const formattedDate = formatLocalDateTime(datePart)

            if (formattedDate !== 'Invalid date') {
                // Replace the original date part with the formatted date
                return text.replace(datePart, formattedDate)
            }
        }

        // Return original text if no date part is found or date is invalid
        return text
    }

    const lastUpdateCell = ({ cell }: CellProps<TableReportItem, string>) => {
        let value = cell.value?.trim()
        const isNoData = cell.row.original?.groupId === undefined

        if (cell.isAggregated || isNoData) {
            return null
        }

        if (!value || value === '-') {
            value = '-'
        } else {
            value = extractAndFormatDate(value)
        }

        return SpanTextWithTooltip(value, '155px')
    }

    const dataDescriptionCell = ({
        cell,
    }: CellProps<TableReportItem, string>) => {
        let value = cell.value?.trim()
        const isNoData = cell.row.original?.groupId === undefined

        if (cell.isAggregated || isNoData) {
            return null
        }

        if (!value || value === '-') {
            value = '-'
        } else {
            // Encode all special characters including line breaks
            value = he.encode(value)
            // Replace line breaks with <br /> tags for HTML rendering
            value = value.replace(/\r\n/g, '<br />')
        }

        // Render content with line breaks
        return (
            <HoverTooltip
                text={<span dangerouslySetInnerHTML={{ __html: value }}></span>}
                enterDelay={700}
                enterNextDelay={700}
            >
                <span
                    className={`${classes.greyCellText} ${classes.offsetSpanForTooltip}`}
                    style={{ minWidth: 155, maxHeight: 60 }}
                    dangerouslySetInnerHTML={{ __html: value }}
                ></span>
            </HoverTooltip>
        )
    }

    const tableColumns = useMemo<SpecColumn<TableReportItem>[]>(
        () => [
            {
                Header: '',
                accessor: 'groupName',
                minWidth: 0,
                width: 0,
            },
            {
                Header: 'DATA TYPE',
                accessor: 'dataType',
                Cell: ({ cell }: CellProps<TableReportItem, string>) => {
                    const value = cell.value?.trim() || '-'
                    const isNoData = cell.row.original?.groupId === undefined

                    if (cell.isAggregated || isNoData) {
                        return null
                    }

                    const colorMapping: Record<string, string> = {
                        Submittals: '#FF0000',
                        'Unresolved Options': '#0085FF',
                    }

                    const barColor = colorMapping[value]

                    return (
                        <span
                            className={`${classes.greyCellText} ${classes.offsetSpanForTooltip}`}
                            style={{ minWidth: '100px' }}
                        >
                            {barColor && (
                                <BarIcon
                                    color={barColor}
                                    className={classes.barIcon}
                                />
                            )}
                            {value}
                        </span>
                    )
                },
                minWidth: 157,
                width: 157,
            },
            {
                Header: 'DATA TITLE',
                accessor: 'dataTitle',
                disableFilters: true,
                minWidth: 157,
                width: 157,
                Cell: ({ cell }: CellProps<TableReportItem, string>) => {
                    let value = cell.value?.trim()
                    const isNoData = cell.row.original?.groupId === undefined

                    if (cell.isAggregated || isNoData) {
                        return null
                    }
                    if (!value || value === '-') {
                        value = '-'
                    }
                    return SpanTextWithTooltip(value, '135px')
                },
            },
            {
                Header: 'DATA DESCRIPTION',
                accessor: 'dataDescription',
                minWidth: 262,
                width: 262,
                Cell: dataDescriptionCell,
            },
            {
                Header: 'SECTION',
                accessor: 'section',
                minWidth: 162,
                width: 212,
                Cell: ({ cell }: CellProps<TableReportItem, string>) => {
                    let value = cell.value?.trim()
                    const { groupId, familyElementId } = cell.row.original || {}
                    const isClickable =
                        (value && value !== '-' && value !== '0') ||
                        familyElementId !== null

                    if (cell.isAggregated || groupId === undefined) {
                        return null
                    }
                    if (!value || value === '-') {
                        value = '-'
                    }

                    return (
                        <HoverTooltip
                            text={value}
                            enterDelay={700}
                            enterNextDelay={700}
                        >
                            <button
                                className={`${classes.buttonOffsetTooltip} ${
                                    isClickable
                                        ? `${classes.blueLinkText} ${classes.clickable}`
                                        : classes.greyCellText
                                }`}
                                style={{ minWidth: '146px' }}
                                onClick={
                                    isClickable
                                        ? (e) => {
                                              e.preventDefault()
                                              e.stopPropagation()
                                              openProjectWorkspaceById(
                                                  familyElementId
                                              )
                                          }
                                        : undefined
                                }
                                tabIndex={0}
                            >
                                {value}
                            </button>
                        </HoverTooltip>
                    )
                },
            },
            {
                Header: 'SECTION SOURCE',
                accessor: 'sectionSource',
                Cell: ({ cell }: CellProps<TableReportItem, string>) => {
                    let value = cell.value?.trim()
                    const isNoData = cell.row.original?.groupId === undefined

                    if (cell.isAggregated || isNoData) {
                        return null
                    }
                    if (!value || value === '-') {
                        value = '-'
                    }

                    return SpanTextWithTooltip(value, '145px')
                },
                minWidth: 142,
                width: 142,
            },
            {
                Header: 'PRODUCT TYPE',
                accessor: 'productType',
                Cell: ({ cell }: CellProps<TableReportItem, string>) => {
                    if (cell.isAggregated) {
                        return null
                    }
                    let value = cell.value?.trim()
                    const { groupId, productTypeElementId } =
                        cell.row.original || {}
                    const isClickable = value && value !== '-' && value !== '0'

                    if (groupId === undefined)
                        return (
                            <span className={classes.greyCellText}>
                                NO DATA TO DISPLAY
                            </span>
                        )
                    if (!value || value === '-') {
                        return (
                            <span className={classes.greyCellText}>
                                {value}
                            </span>
                        )
                    }

                    return (
                        <HoverTooltip
                            text={value}
                            enterDelay={700}
                            enterNextDelay={700}
                        >
                            <button
                                className={`${classes.buttonOffsetTooltip} ${
                                    isClickable
                                        ? `${classes.blueLinkText} ${classes.clickable}`
                                        : classes.greyCellText
                                }`}
                                style={{ minWidth: '150px' }}
                                onClick={
                                    isClickable
                                        ? (e) => {
                                              e.preventDefault()
                                              e.stopPropagation()
                                              openProjectWorkspaceById(
                                                  productTypeElementId
                                              )
                                          }
                                        : undefined
                                }
                                tabIndex={0}
                            >
                                {value}
                            </button>
                        </HoverTooltip>
                    )
                },
                minWidth: 172,
                width: 172,
            },
            {
                Header: 'PRODUCT SELECTION',
                accessor: 'productSelection',
                Cell: ({ cell }: CellProps<TableReportItem, string>) => {
                    let value = cell.value?.trim()
                    const { groupId, productTypeElementId, dataType } =
                        cell.row.original || {}
                    const isClickable = value && value !== '-' && value !== '0'

                    if (cell.isAggregated || groupId === undefined) {
                        return null
                    }

                    if (!value || value === '-') {
                        return (
                            <span
                                data-testid="productSelectionCell"
                                className={classes.greyCellText}
                                aria-hidden="true"
                            >
                                -
                            </span>
                        )
                    }

                    if (
                        dataType === ReportTypes.UnresolvedOptions &&
                        value === '-'
                    ) {
                        return (
                            <span
                                data-testid="productSelectionCell"
                                className={classes.greyCellText}
                                aria-hidden="true"
                            >
                                -
                            </span>
                        )
                    }

                    return (
                        <button
                            data-testid="productSelectionCell"
                            className={`${classes.pillText} ${
                                isClickable && classes.clickable
                            }`}
                            onClick={
                                isClickable
                                    ? (e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          openProjectWorkspaceById(
                                              productTypeElementId
                                          )
                                      }
                                    : undefined
                            }
                            aria-label={`View details for product: ${value}`}
                        >
                            {`Products: ${value}`}
                        </button>
                    )
                },
                minWidth: 172,
                width: 172,
            },
            {
                Header: 'ARTICLE',
                accessor: 'article',
                Cell: ({ cell }: CellProps<TableReportItem, string[]>) => {
                    const isNoData = cell.row.original?.groupId === undefined
                    const value = cell?.value

                    if (isNoData || !value) {
                        return null
                    }
                    return (
                        <HoverTooltip
                            text={value}
                            enterDelay={700}
                            enterNextDelay={700}
                        >
                            <span
                                className={`${classes.greyCellText} ${classes.offsetSpanForTooltip}`}
                                style={{ minWidth: '150px' }}
                            >
                                {value}
                            </span>
                        </HoverTooltip>
                    )
                },
                minWidth: 162,
                width: 162,
            },
            {
                Header: 'DRAWING DESIGNATION',
                accessor: 'drawingDesignation',
                Cell: ({ cell }: CellProps<TableReportItem, string>) => {
                    let value = cell.value?.trim()

                    const isNoData = cell.row.original?.groupId === undefined
                    if (cell.isAggregated || isNoData) {
                        return null
                    }
                    if (!value || value === '-') {
                        value = '-'
                    }

                    return SpanTextWithTooltip(value, '160px')
                },
                minWidth: 182,
                width: 182,
            },
            // BUG 2280494: Temporarily hiding TAG column (contains LOS values)
            // Per Chris Metropulos request to hide the column for the time being
            // TAG column width (120px) redistributed equally: +12px added to each of the 10 remaining columns
            // {
            //     Header: 'TAG',
            //     accessor: 'tag',
            //     Cell: ({ cell }: CellProps<TableReportItem, string>) => {
            //         let value = cell.value?.trim()
            //         const isNoData = cell.row.original?.groupId === undefined

            //         if (cell.isAggregated || isNoData) {
            //             return null
            //         }
            //         if (!value || value === '-') {
            //             value = '-'
            //         }
            //         return SpanTextWithTooltip(value, '100px')
            //     },
            //     minWidth: 120,
            //     width: 120,
            // },
            {
                Header: 'LAST UPDATED',
                accessor: 'lastUpdate',
                Cell: lastUpdateCell,
                minWidth: 162,
                width: 162,
            },
        ],
        []
    )

    /**
     * useSpecTableOptions give you insight of all the exposed props that you can
     * do within the SpecTable.
     */
    const tableProps: UseSpecTableOptionProps<TableReportItem> = React.useMemo(() => {
        const reportData = getStructuredTableView(reportsData, divisions) ?? []
        const tableSettings: TableSettingsProps<TableReportItem> = {
            initialPinningProps: {
                firstColumnPinned: false,
                lastColumnPinned: false,
            },
        }
        const specTableTitleSettings: SpecTableTitleSettingsProps<TableReportItem> = {
            hasGrouping: true,
            showFiltersRow: false,
            showTableSettings: false,
            tableViewActive: true,
            theme: 'light',
            tableSettings,
            tableTitle: projectData?.getProject?.name,
            onFilterClick: undefined,
            onExpandAllClick: undefined,
            onRefreshClick: generateReport,
            lastUpdate: lastUpdate,
            onReportsExportClick: exportReports,
            isExportLoading: isExportLoading,
        }

        return {
            allowGrouping: true,
            columns: tableColumns,
            data: reportData,
            divContainerRef: divContainer,
            expanded: {},
            fetchData,
            fetchDataLoading: !!tableDataLoading,
            fetchNextDataLoading: false,
            groupBy,
            height: 'auto',
            onStateChange: setTableState,
            pageSize: 25,
            showCheckboxColumn: false,
            specTableTitleSettings,
            totalItems: reportData.length,
            sortBy: initialSorting,
            defaultColumn: {
                disableSortBy: true,
            } as Column<TableReportItem>,
            isProjectHome: false,
            showTVSettings: true,
        }
    }, [
        inputFilterValues,
        reportsData,
        tableColumns,
        fetchNextData,
        fetchData,
        lastUpdate,
        tableDataLoading,
        isExportLoading,
    ])
    const specTableProps = useSpecTableOptions<TableReportItem>(tableProps)
    return (
        <>
            <GridContainer
                className={classes.tableViewCont}
                data-testid={'reportTableContainer'}
            >
                <GridItem
                    xs={12}
                    ref={divContainer}
                    className={classes.specTableStyle}
                >
                    <SpecTable
                        {...specTableProps}
                        lazyLoading={true}
                        height={800}
                        enablePagination={true}
                    />
                </GridItem>
            </GridContainer>
        </>
    )
}

/**
 * Function to get structured table view.
 * @param {SmartReportsItem[]} projectReportData - The project report data.
 * @returns {TableReportItem[]} - The mapped TableReport.
 */
const getStructuredTableView = (
    projectReportData: SmartReportsItem[] = [],
    divisions: SmartReportDivision[] = []
) => {
    const mappedTableReport: TableReportItem[] = []

    // map the projectReportData to TableReportItem
    projectReportData.forEach(
        ({
            division,
            divisionGroupId,
            reportType,
            ...rest
        }: SmartReportsItem) => {
            // to not add multiple rows with "no data" for the same groupName
            const isNoDataAlreadyAdded = !mappedTableReport.some(
                (x) => x.groupName === division && x.groupId === null
            )

            if (divisionGroupId !== null || isNoDataAlreadyAdded) {
                mappedTableReport.push({
                    ...rest,
                    groupName: division,
                    groupId: divisionGroupId,
                    dataType: reportType,
                })
            }
        }
    )

    // to remove "no data" rows that have a group with data, then, sort by groupName
    const removeNoDataWithReportData = mappedTableReport.filter(
        (filterItem) => {
            const hasReportData = mappedTableReport.some(
                (report) =>
                    report.groupName === filterItem.groupName &&
                    report.groupId !== null
            )

            if (filterItem.groupId === null && hasReportData) {
                return false
            } else {
                return true
            }
        }
    )

    const mappedWithAllDivisions: TableReportItem[] = []
    // map the TableReportItem to include all divisions
    divisions.forEach(({ groupId, name }) => {
        const reportItems = removeNoDataWithReportData.filter(
            (x) => x.groupId === groupId
        )

        if (!reportItems.some(Boolean)) {
            mappedWithAllDivisions.push({
                groupId: undefined,
                groupName: name,
                dataType: undefined,
                article: undefined,
                dataDescription: undefined,
                dataTitle: undefined,
                drawingDesignation: undefined,
                familyElementId: undefined,
                groupCount: 0,
                lastUpdate: undefined,
                productSelection: undefined,
                productType: undefined,
                productTypeElementId: undefined,
                section: undefined,
                sectionSource: undefined,
                tag: undefined,
            })
        } else {
            mappedWithAllDivisions.push(...reportItems)
        }
    })

    return (
        mappedWithAllDivisions
            // format groupName to include group count
            .map((mappedItem) => {
                const sections = new Set(
                    projectReportData
                        .filter(
                            (report) =>
                                report.division === mappedItem.groupName &&
                                report.divisionGroupId !== null
                        )
                        .map((report) => report.section)
                )
                const count = sections.size
                mappedItem.groupCount = count
                mappedItem.groupName = `${mappedItem.groupName} (${
                    mappedItem.groupId ? count : 0
                } Section${count !== 1 ? 's' : ''})`
                return mappedItem
            })
            // make sure to sort ascending by groupName
            .sort((a, b) =>
                (a.groupName ?? '').localeCompare(b.groupName ?? '')
            )
    )
}
export default ProjectReportsTable
